import { App as Application } from '@capacitor/app';
import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * La parole qui sauve une mise a jour.
 *
 * L'application iOS peut remplacer son interface sans passer par l'App
 * Store (`ios/App/App/MiseAJour.swift`). Le risque de ce pouvoir est
 * connu : une version qui ne demarre pas laisse un ecran blanc, et plus
 * rien ne peut la remplacer puisque c'est justement elle qui devait le
 * faire.
 *
 * D'ou ce filet, et il tient a une ligne : la page dit qu'elle a demarre.
 * Sans cette parole, la coquille jette la version au lancement suivant et
 * revient a celle qu'elle embarque, celle qu'Apple a relue.
 *
 * L'appel se fait donc le plus tard possible dans le demarrage — apres
 * que l'arbre React est monte, pas avant — parce que sa valeur tient
 * entierement a ce qu'il prouve. Le dire trop tot reviendrait a valider
 * une version qui ne s'affiche pas.
 */
export interface EtatDeLaMaj {
  /** L'interface servie, ou celle du paquet si aucune n'a ete posee. */
  interface: string;
  /** Celle que l'application embarque, telle qu'elle l'annonce. */
  paquet: string;
  demarree: boolean;
  /** Ce que la derniere passe a fait, en une phrase datee. */
  compteRendu: string;
  /** Le numero de build de la coquille, celui qu'Apple connait. */
  coquille: number;
  /** Sa version affichable, « 1.6 ». */
  version: string;
}

interface MiseAJourPlugin {
  demarree(): Promise<void>;
  etat(): Promise<EtatDeLaMaj>;
}

const greffon = registerPlugin<MiseAJourPlugin>('MiseAJour');

/** Sans coquille native, il n'y a pas de version a confirmer. */
const disponible = () => Capacitor.getPlatform() === 'ios';

export function direQueLInterfaceADemarre(): void {
  if (!disponible()) return;
  // Un echec ne se rattrape pas : la coquille jettera la version, ce qui
  // est exactement ce qu'on veut si la page ne sait plus lui parler.
  void greffon.demarree().catch(() => undefined);
}

/**
 * Ce que l'application sert, ce qu'elle embarque, et ce que la derniere
 * passe a fait.
 *
 * Le journal de l'appareil ne se lit qu'avec un cable et un Mac, et il
 * s'est deja derobe le jour ou il fallait comprendre pourquoi une version
 * n'arrivait pas. D'ou ce compte rendu, que l'application peut lire et
 * dire sans outil. Les reglages l'affichaient ; ils n'en gardent que la
 * version, ecrite sous le compte a chaque demarrage — voir
 * `users.noterMaVersion`. Le reste attend ici le jour ou il faudra
 * comprendre.
 */
export async function etatDeLaMiseAJour(): Promise<EtatDeLaMaj | null> {
  if (!disponible()) return null;
  const brut = await greffon.etat().catch(() => null);
  if (!brut) return null;
  /*
    Une coquille d'avant cette version ne connait ni `interface` ni
    `coquille` : ce qui s'appelle aujourd'hui `interface` s'appelait alors
    `version`. Or l'interface peut arriver par le moteur de mise a jour sur
    une telle coquille, et lire `interface.slice` sur un champ absent
    faisait tomber l'ecran des reglages. On traduit donc ici, une fois,
    plutot que partout ou l'on lit. Le numero de build inconnu vaut zero :
    c'est a l'annonce de ne rien en conclure.
  */
  if (typeof brut.interface !== 'string') {
    return { ...brut, interface: String(brut.version ?? ''), version: '?', coquille: 0 };
  }
  return brut;
}

/** Le numero de build de l'application installee, et ou elle vit. */
export interface MaCoquille {
  plateforme: 'ios' | 'android';
  /** Zero quand il est inconnu : c'est a l'annonce de ne rien en conclure. */
  numero: number;
}

/**
 * Quel build porte ce telephone.
 *
 * Sur iPhone, le greffon de mise a jour le dit, avec le reste. Sur
 * Android ce greffon n'existe pas — l'interface y voyage dans le paquet,
 * sans moteur pour la remplacer — mais Capacitor connait le
 * `versionCode`, le meme numero que celui du Play Store. Sur le web, il
 * n'y a pas de build : `null`.
 */
export async function maCoquille(): Promise<MaCoquille | null> {
  const plateforme = Capacitor.getPlatform();
  if (plateforme === 'ios') {
    const etat = await etatDeLaMiseAJour();
    return etat ? { plateforme, numero: etat.coquille } : null;
  }
  if (plateforme === 'android') {
    const info = await Application.getInfo().catch(() => null);
    return info ? { plateforme, numero: Number(info.build) || 0 } : null;
  }
  return null;
}
